/**
 * Guest Mode Context
 * Manages local-only mode with SQLite database and user profile
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { sqliteDb } from '~/lib/sqliteAdapter/database';
import { v4 as uuidv4 } from 'uuid';
import { getCurrencyFromLocale } from '~/lib/utils/currencyDetection';

export interface UserProfile {
  firstName: string;
  lastName: string;
  avatar?: string;
  birthDate?: string;
  baseCurrency: string;
}

interface GuestModeContextType {
  isGuestDbReady: boolean;
  guestUserId: string;
  userProfile: UserProfile;
  updateUserProfile: (updates: Partial<UserProfile>) => Promise<void>;
  clearAllData: () => Promise<void>;
}

const GuestModeContext = createContext<GuestModeContextType | undefined>(undefined);

const GUEST_USER_ID = 'guest-user-00000000-0000-0000-0000-000000000000';
const GUEST_SETTINGS_ID = 'guest-settings-id';

const DEFAULT_PROFILE: UserProfile = {
  firstName: '',
  lastName: '',
  baseCurrency: getCurrencyFromLocale()
};


console.log('DEFAULT_PROFILE', DEFAULT_PROFILE);

interface GuestModeProviderProps {
  children: ReactNode;
}

export function GuestModeProvider({ children }: GuestModeProviderProps) {
  const [isGuestDbReady, setIsGuestDbReady] = useState<boolean>(false);
  const [userProfile, setUserProfile] = useState<UserProfile>(DEFAULT_PROFILE);

  // Auto-initialize on mount
  useEffect(() => {
    initializeGuestMode();
  }, []);

  const initializeGuestMode = async () => {
    try {
      console.log('🔄 Initializing local-only mode...');
      
      // Initialize SQLite database
      await sqliteDb.initialize();
      
      // Load user profile from database
      await loadUserProfile();
      
      setIsGuestDbReady(true);
      
      console.log('✅ Local-only mode ready');
    } catch (error) {
      console.error('❌ Failed to initialize local-only mode:', error);
      throw error;
    }
  };

  const loadUserProfile = async () => {
    try {
      const db = sqliteDb.getDatabase();
      const result = db.exec(
        `SELECT first_name, last_name, avatar, birth_date, base_currency 
         FROM user_settings 
         WHERE user_id = ? LIMIT 1`,
        [GUEST_USER_ID]
      );

      if (result[0] && result[0].values.length > 0) {
        const [firstName, lastName, avatar, birthDate, baseCurrency] = result[0].values[0];
        
        setUserProfile({
          firstName: firstName as string || DEFAULT_PROFILE.firstName,
          lastName: lastName as string || DEFAULT_PROFILE.lastName,
          avatar: avatar as string | undefined,
          birthDate: birthDate as string | undefined,
          baseCurrency: baseCurrency as string || DEFAULT_PROFILE.baseCurrency,
        });
        
        console.log('✅ Loaded user profile from database');
      } else {
        // Use defaults if no profile exists
        setUserProfile(DEFAULT_PROFILE);
        console.log('ℹ️ Using default user profile');
      }
    } catch (error) {
      console.error('❌ Failed to load user profile:', error);
      setUserProfile(DEFAULT_PROFILE);
    }
  };

  const updateUserProfile = async (updates: Partial<UserProfile>) => {
    try {
      const db = sqliteDb.getDatabase();
      const now = new Date().toISOString();
      
      // Merge updates with current profile
      const newProfile = { ...userProfile, ...updates };
      
      // Use INSERT OR REPLACE to handle both create and update (UPSERT)
      const query = `
        INSERT OR REPLACE INTO user_settings (
          id, user_id, first_name, last_name, avatar, birth_date, base_currency, 
          status, date_created, date_updated
        ) VALUES (?, ?, ?, ?, ?, ?, ?, 'published', COALESCE(
          (SELECT date_created FROM user_settings WHERE user_id = ?), ?
        ), ?)
      `;
      
      const values = [
        GUEST_SETTINGS_ID,
        GUEST_USER_ID,
        newProfile.firstName,
        newProfile.lastName,
        newProfile.avatar || null,
        newProfile.birthDate || null,
        newProfile.baseCurrency,
        GUEST_USER_ID, // For COALESCE subquery
        now, // Fallback date_created if new record
        now  // date_updated
      ];
      
      const stmt = db.prepare(query);
      stmt.bind(values);
      stmt.step();
      stmt.free();
      
      sqliteDb.save();
      
      // Update local state
      setUserProfile(newProfile);
      
      console.log('✅ User profile saved to database:', { 
        firstName: newProfile.firstName, 
        lastName: newProfile.lastName,
        birthDate: newProfile.birthDate,
        baseCurrency: newProfile.baseCurrency
      });
    } catch (error) {
      console.error('❌ Failed to update user profile:', error);
      throw error;
    }
  };

  const clearAllData = async () => {
    try {
      console.log('🧹 Clearing all local data...');
      
      // Clear the database
      sqliteDb.clear();
      
      // Reload the page to reset everything
      window.location.reload();
    } catch (error) {
      console.error('❌ Failed to clear data:', error);
      throw error;
    }
  };

  const value: GuestModeContextType = {
    isGuestDbReady,
    guestUserId: GUEST_USER_ID,
    userProfile,
    updateUserProfile,
    clearAllData,
  };

  return (
    <GuestModeContext.Provider value={value}>
      {children}
    </GuestModeContext.Provider>
  );
}

export function useGuestMode() {
  const context = useContext(GuestModeContext);
  if (context === undefined) {
    throw new Error('useGuestMode must be used within a GuestModeProvider');
  }
  return context;
}
